import React from 'react';
import {
    ClosingCountdown,
    FeeAgreement,
    FeeAgreementParty,
    FeeLineItem,
    VetValueFeeLedger,
} from '../../../models/FlowOfFunds/flow-of-funds.model';
import { LevelOfService, isSfas } from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { SfasFeeParams } from '../../../slices/marketplace/loi-comparison/consideration-fees';
import {
    VvConnectFeeRowDto,
    VvConnectFeesDto,
} from '../../../slices/marketplace/loi-comparison/models/vv-connect-fees.model';
import {
    VvConnectFeesLabelsColumn,
    VvConnectFeesSection,
} from '../../../containers/ConnectMarketplaceDashboard/LoiComparisonTab/LoiComparisonSummary/VvConnectFeesSection';
import { FeeAgreementBlock } from '../FeeAgreementBlock/FeeAgreementBlock';
import { ClosingTickler } from '../ClosingTickler/ClosingTickler';
import '../../../containers/ConnectMarketplaceDashboard/LoiComparisonTab/LoiComparisonSummary/LoiComparisonSummary.scss';
import './VetValueFees.scss';

const EMPTY_ROW: VvConnectFeeRowDto = { fee: null, credits: null };

const toFeeRow = (item: FeeLineItem): VvConnectFeeRowDto => ({ fee: item.fee, credits: item.credit });

const sumProcessFees = (items: FeeLineItem[]): VvConnectFeeRowDto =>
    items.reduce<VvConnectFeeRowDto>(
        (total, item) => ({
            fee: (total.fee ?? 0) + (item.fee ?? 0),
            credits: (total.credits ?? 0) + (item.credit ?? 0),
        }),
        { fee: null, credits: null }
    );

const toVvConnectFeesDto = (fees: VetValueFeeLedger): VvConnectFeesDto => ({
    transactionFeePercent: { fee: fees.transactionFeePercent, credits: null },
    transactionFairValue: { fee: fees.projectedTransactionValue, credits: null },
    transactionFeeDollar: { fee: fees.projectedTransactionFee, credits: null },
    applicationFee: toFeeRow(fees.applicationFee),
    monthlyRetainerFees: toFeeRow(fees.monthlyRetainerFees),
    processFees: sumProcessFees(fees.processFees),
    buyerIdNdaFees: EMPTY_ROW,
    pipPiqProvisionFees: EMPTY_ROW,
    dfmProvisionFees: EMPTY_ROW,
    lateDelayFees: toFeeRow(fees.lateDelayFees),
    engagementSuspensionFees: toFeeRow(fees.engagementSuspensionFees),
    totalNetFee: EMPTY_ROW,
    percentOfFairValue: EMPTY_ROW,
    netFeePaymentAtClosing: EMPTY_ROW,
});

const VETVALUE_SEAT_LABEL = 'VetValue';

const isVetValueSeat = (party: FeeAgreementParty) => party.label === VETVALUE_SEAT_LABEL;

const holdSellerSeatUntilVetValueAgrees = (agreement: FeeAgreement): FeeAgreement => {
    if (agreement.parties.some((party) => isVetValueSeat(party) && party.hasAgreed)) {
        return agreement;
    }
    return {
        ...agreement,
        parties: agreement.parties.map((party) =>
            isVetValueSeat(party) ? party : { ...party, canAgree: false }
        ),
    };
};

const buildAgreementDescription = (agreement: FeeAgreement): string => {
    const labels = agreement.parties.map((party) => party.label);
    return labels.length === 0
        ? 'These fees must be agreed before they are finalized.'
        : `${labels.join(' and ')} must agree to these fees before they are finalized.`;
};

interface VetValueFeesProps {
    fees: VetValueFeeLedger;
    levelOfService: LevelOfService;
    onAgree?: () => void;
    isSaving?: boolean;
    closing?: ClosingCountdown | null;
    sfasParams?: SfasFeeParams | null;
}

export const VetValueFees = ({
    fees,
    levelOfService,
    onAgree,
    isSaving,
    closing,
    sfasParams,
}: VetValueFeesProps) => {
    const agreement = holdSellerSeatUntilVetValueAgrees(fees.agreement);

    return (
        <div className="ff-vvfees">
            <div className="ff-vvfees__tickler-slot">
                <ClosingTickler closing={closing} />
            </div>

            <div className="loi-comparison-summary">
                <div className="summary-inner vv-fees-section">
                    <VvConnectFeesLabelsColumn isSfasEngagement={isSfas(levelOfService)} />
                    <VvConnectFeesSection
                        fees={toVvConnectFeesDto(fees)}
                        portionPaidAtClose={fees.portionPaidAtClose}
                        projectedTransactionValue={fees.projectedTransactionValue}
                        projectedTransactionFee={fees.projectedTransactionFee}
                        levelOfService={levelOfService}
                        sfasParams={sfasParams}
                        totalCreditsOverride={fees.totalCredits}
                        netProjectedTotalFeesOverride={fees.netProjectedTotalFees}
                    />
                </div>
            </div>

            <FeeAgreementBlock
                agreement={agreement}
                description={buildAgreementDescription(agreement)}
                onAgree={onAgree}
                isSaving={isSaving}
            />
        </div>
    );
};
